import { useCallback, useEffect, useMemo, useState } from 'react';
import { bankingAPI, reconciliationAPI, accountingAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import {
  Landmark,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  RefreshCcw,
} from 'lucide-react';

/**
 * Loan repayment modal (plan §6 / §12).
 *
 * Lets the user match an outgoing statement line on a bank/cash account to
 * the next scheduled instalment of a loan. Core responsibilities:
 *
 *   1. Pick the loan account (a bank_account with type=loan_account that has
 *      a loan_account_details row + amortisation schedule).
 *   2. Fetch the next N unpaid schedule lines and highlight the one whose
 *      due_date is closest to the statement-line date.
 *   3. Pre-fill principal + interest from the chosen schedule row. If the
 *      statement amount does not equal the scheduled payment, show a
 *      variance banner and let the user:
 *         - tweak the principal / interest split (sum must equal |amount|)
 *         - re-amortise the tail of the schedule
 *   4. Pick the interest nominal (defaults to details.interest_nominal_account_id).
 *   5. Submit -> POST /reconciliation/loans/repayment, which creates a
 *      draft match with principal (target=loan_schedule_line) + interest
 *      (target=nominal_category) allocations, ready to reconcile.
 */

const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
};

const shortDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

export function LoanRepaymentModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onMatched,
}) {
  const lineAmount = Number(line?.amount ?? 0);
  const statementAbs = round2(Math.abs(lineAmount));

  const [loanAccounts, setLoanAccounts] = useState([]);
  const [loanAccountsError, setLoanAccountsError] = useState('');
  const [loadingLoans, setLoadingLoans] = useState(false);

  const [selectedLoanId, setSelectedLoanId] = useState('');
  const [loanDetails, setLoanDetails] = useState(null);
  const [scheduleLines, setScheduleLines] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [principalOverride, setPrincipalOverride] = useState('');
  const [interestOverride, setInterestOverride] = useState('');
  const [reamortise, setReamortise] = useState(false);

  const [interestAccounts, setInterestAccounts] = useState([]);
  const [interestAccountId, setInterestAccountId] = useState('');

  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const resetFormState = useCallback(() => {
    setSelectedLoanId('');
    setLoanDetails(null);
    setScheduleLines([]);
    setSelectedScheduleId('');
    setPrincipalOverride('');
    setInterestOverride('');
    setReamortise(false);
    setInterestAccountId('');
    setReference(line?.reference || '');
    setNotes('');
    setError('');
    setDone(null);
    setBusy(false);
    setScheduleError('');
  }, [line?.reference]);

  useEffect(() => {
    if (!open) return;
    resetFormState();
  }, [open, line?.id, resetFormState]);

  // Load the tenant's loan_account bank accounts (Phase 3).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingLoans(true);
        setLoanAccountsError('');
        const list = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        const loans = (list || []).filter((a) => a.account_type === 'loan_account');
        setLoanAccounts(loans);
        if (loans.length === 1) {
          setSelectedLoanId(loans[0].id);
        }
      } catch (err) {
        if (cancelled) return;
        setLoanAccountsError(
          err?.response?.data?.error || err.message || 'Could not load loan accounts'
        );
      } finally {
        if (!cancelled) setLoadingLoans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Load the interest expense nominal accounts (type=expense) once.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await accountingAPI.getAccounts({ type: 'expense', active_only: true });
        if (cancelled) return;
        setInterestAccounts(Array.isArray(list) ? list : []);
      } catch {
        // Non-fatal; the user can still pick via the free-text fallback if the
        // COA is not yet seeded.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // When the loan account changes, pull details + next schedule lines.
  useEffect(() => {
    if (!open || !selectedLoanId) {
      setLoanDetails(null);
      setScheduleLines([]);
      setSelectedScheduleId('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingSchedule(true);
        setScheduleError('');
        const [acct, nextRes] = await Promise.all([
          reconciliationAPI.getLoanAccount(selectedLoanId),
          reconciliationAPI.getNextLoanLines(selectedLoanId, 5),
        ]);
        if (cancelled) return;
        setLoanDetails(acct?.details || null);
        const lines = nextRes?.schedule_lines || [];
        setScheduleLines(lines);

        // Auto-select the line with due_date closest to the statement date.
        if (lines.length > 0) {
          let bestId = lines[0].id;
          if (line?.transaction_date) {
            const target = new Date(line.transaction_date).getTime();
            let bestDiff = Infinity;
            for (const l of lines) {
              const diff = Math.abs(new Date(l.due_date).getTime() - target);
              if (diff < bestDiff) {
                bestDiff = diff;
                bestId = l.id;
              }
            }
          }
          setSelectedScheduleId(bestId);
        } else {
          setSelectedScheduleId('');
        }

        // Pre-populate interest nominal from details (if any).
        if (acct?.details?.interest_nominal_account_id) {
          setInterestAccountId(acct.details.interest_nominal_account_id);
        }
      } catch (err) {
        if (cancelled) return;
        setScheduleError(
          err?.response?.data?.error || err.message || 'Could not load loan schedule'
        );
      } finally {
        if (!cancelled) setLoadingSchedule(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedLoanId, line?.transaction_date]);

  const selectedLine = useMemo(
    () => scheduleLines.find((l) => l.id === selectedScheduleId) || null,
    [scheduleLines, selectedScheduleId]
  );

  // Derive principal/interest. Overrides win; otherwise use the scheduled
  // amounts (or prorate them if the statement amount differs).
  const derived = useMemo(() => {
    if (!selectedLine) return { principal: 0, interest: 0, variance: 0 };
    const scheduledPrincipal = Number(selectedLine.principal_portion || 0);
    const scheduledInterest = Number(selectedLine.interest_portion || 0);
    const scheduledPayment = Number(selectedLine.scheduled_payment || 0);

    if (principalOverride !== '' || interestOverride !== '') {
      const p =
        principalOverride !== ''
          ? Number(principalOverride)
          : round2(statementAbs - Number(interestOverride || 0));
      const i =
        interestOverride !== ''
          ? Number(interestOverride)
          : round2(statementAbs - p);
      return {
        principal: p,
        interest: i,
        variance: round2(statementAbs - scheduledPayment),
      };
    }
    // If the statement amount matches the scheduled payment exactly, use
    // the scheduled split verbatim.
    if (Math.abs(statementAbs - scheduledPayment) < 0.01) {
      return {
        principal: scheduledPrincipal,
        interest: scheduledInterest,
        variance: 0,
      };
    }
    // Otherwise default to assigning the full interest and letting
    // principal absorb the variance -- clamp to >= 0.
    let principal = round2(statementAbs - scheduledInterest);
    let interest = scheduledInterest;
    if (principal < 0) {
      principal = 0;
      interest = statementAbs;
    }
    return {
      principal,
      interest,
      variance: round2(statementAbs - scheduledPayment),
    };
  }, [selectedLine, statementAbs, principalOverride, interestOverride]);

  const splitSum = round2(Number(derived.principal) + Number(derived.interest));
  const splitMatches = Math.abs(splitSum - statementAbs) < 0.01;

  const canSubmit =
    !!line &&
    !!bankAccountId &&
    !!selectedLoanId &&
    !!selectedScheduleId &&
    !!interestAccountId &&
    splitMatches &&
    !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        statement_line_id: line.id,
        schedule_line_id: selectedScheduleId,
        interest_nominal_account_id: interestAccountId,
        reamortise: !!reamortise,
        reference: reference || null,
        notes: notes || null,
      };
      // Only send overrides when user actually touched them.
      if (principalOverride !== '') body.principal_override = Number(principalOverride);
      if (interestOverride !== '') body.interest_override = Number(interestOverride);

      const result = await reconciliationAPI.createLoanRepayment(body);
      setDone({
        match: result.match,
        principal: result.principal,
        interest: result.interest,
        reamortised: !!result.reamortised,
      });
      if (onMatched) onMatched(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not create loan match');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Match loan repayment">
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Statement line summary */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <Landmark className="h-3.5 w-3.5" />
                Statement line (outgoing repayment)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>{shortDate(line.transaction_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate" title={line.description || ''}>
                    {line.description || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Reference</div>
                  <div>{line.reference || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Counterparty</div>
                  <div className="truncate">{line.counterparty || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div
                    className={
                      lineAmount >= 0
                        ? 'text-green-700 dark:text-green-400 font-semibold'
                        : 'text-red-700 dark:text-red-400 font-semibold'
                    }
                  >
                    {formatMoney(lineAmount)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs">
                <div className="flex items-center gap-2 font-semibold text-green-800 dark:text-green-200">
                  <CheckCircle2 className="h-4 w-4" />
                  Loan repayment matched (draft).
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-green-800 dark:text-green-200">
                  <div>
                    Principal: <strong>{formatMoney(done.principal)}</strong>
                  </div>
                  <div>
                    Interest: <strong>{formatMoney(done.interest)}</strong>
                  </div>
                </div>
                {done.reamortised && (
                  <div className="mt-2 text-[11px] text-green-800 dark:text-green-200">
                    The remaining schedule was re-amortised to reflect this variance.
                  </div>
                )}
                <div className="mt-3">
                  <Button size="sm" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}
              {loanAccountsError && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{loanAccountsError}</span>
                  </CardContent>
                </Card>
              )}

              {/* Loan account picker */}
              <div>
                <FormLabel>Loan account</FormLabel>
                <Select
                  value={selectedLoanId}
                  onChange={(e) => setSelectedLoanId(e.target.value)}
                  disabled={loadingLoans}
                >
                  <option value="">
                    {loadingLoans ? 'Loading loans…' : 'Select loan account…'}
                  </option>
                  {loanAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name}
                    </option>
                  ))}
                </Select>
                {!loadingLoans && loanAccounts.length === 0 && !loanAccountsError && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    No loan accounts found. Create one on the banking page first.
                  </div>
                )}
              </div>

              {/* Loan summary */}
              {selectedLoanId && !loadingSchedule && loanDetails && (
                <Card className="border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40">
                  <CardContent className="p-3 text-[11px]">
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Lender</div>
                        <div className="truncate">{loanDetails.lender_name || '—'}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Principal</div>
                        <div>{formatMoney(loanDetails.principal_amount)}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Rate</div>
                        <div>{Number(loanDetails.interest_rate).toFixed(2)}% APR</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Term</div>
                        <div>{loanDetails.term_months}m ({loanDetails.interest_type?.replace('_', ' ')})</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Schedule line picker */}
              <div>
                <FormLabel>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Schedule line to apply this payment to
                  </span>
                </FormLabel>
                {loadingSchedule ? (
                  <div className="text-xs text-gray-500">Loading schedule…</div>
                ) : scheduleError ? (
                  <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                    <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{scheduleError}</span>
                    </CardContent>
                  </Card>
                ) : scheduleLines.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    {selectedLoanId
                      ? 'No unpaid schedule lines found. Configure the loan terms first.'
                      : 'Pick a loan account above.'}
                  </div>
                ) : (
                  <div className="max-h-56 space-y-1 overflow-auto rounded border border-gray-200 p-1 dark:border-gray-700">
                    {scheduleLines.map((l) => {
                      const selected = selectedScheduleId === l.id;
                      return (
                        <label
                          key={l.id}
                          className={
                            'flex cursor-pointer items-start gap-2 rounded p-2 text-xs ' +
                            (selected
                              ? 'bg-purple-100 ring-1 ring-purple-400 dark:bg-purple-900/40'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800')
                          }
                        >
                          <input
                            type="radio"
                            name="loan-schedule-line"
                            className="mt-0.5"
                            checked={selected}
                            onChange={() => setSelectedScheduleId(l.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="font-medium">
                                #{l.sequence} · due {shortDate(l.due_date)} ·{' '}
                                <span>{formatMoney(l.scheduled_payment)}</span>
                              </div>
                              <div className="text-[10px] uppercase tracking-wide text-gray-500">
                                {l.status}
                              </div>
                            </div>
                            <div className="mt-0.5 text-gray-600 dark:text-gray-400">
                              Principal {formatMoney(l.principal_portion)} · Interest{' '}
                              {formatMoney(l.interest_portion)} · Balance after{' '}
                              {formatMoney(l.balance_after)}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Variance banner */}
              {selectedLine && (
                <Card
                  className={
                    Math.abs(derived.variance) < 0.01
                      ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/40'
                      : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30'
                  }
                >
                  <CardContent className="p-3 text-[11px]">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Scheduled</div>
                        <div>{formatMoney(selectedLine.scheduled_payment)}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Paid</div>
                        <div className="font-semibold">{formatMoney(statementAbs)}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-gray-500">Variance</div>
                        <div
                          className={
                            Math.abs(derived.variance) < 0.01
                              ? ''
                              : 'font-semibold text-amber-800 dark:text-amber-200'
                          }
                        >
                          {formatMoney(derived.variance)}
                        </div>
                      </div>
                    </div>
                    {Math.abs(derived.variance) >= 0.01 && (
                      <div className="mt-2 flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
                        <div className="text-[11px] text-amber-800 dark:text-amber-200">
                          The paid amount differs from the scheduled instalment. Principal
                          absorbs the variance by default — adjust the split below if
                          needed. Tick &quot;re-amortise remainder&quot; to recalculate the
                          remaining schedule after this payment.
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Principal / interest split */}
              {selectedLine && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FormLabel>Principal</FormLabel>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={principalOverride !== '' ? principalOverride : derived.principal.toFixed(2)}
                      onChange={(e) => setPrincipalOverride(e.target.value)}
                    />
                  </div>
                  <div>
                    <FormLabel>Interest</FormLabel>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={interestOverride !== '' ? interestOverride : derived.interest.toFixed(2)}
                      onChange={(e) => setInterestOverride(e.target.value)}
                    />
                  </div>
                  {!splitMatches && (
                    <div className="col-span-2 text-[11px] text-red-700 dark:text-red-400">
                      Principal + interest = {formatMoney(splitSum)} but statement shows{' '}
                      {formatMoney(statementAbs)}.
                    </div>
                  )}
                </div>
              )}

              {/* Re-amortise toggle */}
              {selectedLine && Math.abs(derived.variance) >= 0.01 && (
                <div className="flex items-center gap-2 text-xs">
                  <input
                    id="reamortise"
                    type="checkbox"
                    checked={reamortise}
                    onChange={(e) => setReamortise(e.target.checked)}
                  />
                  <label htmlFor="reamortise" className="inline-flex items-center gap-1">
                    <RefreshCcw className="h-3 w-3" />
                    Re-amortise the remaining schedule after this payment
                  </label>
                </div>
              )}

              {/* Interest nominal */}
              <div>
                <FormLabel>Interest expense nominal</FormLabel>
                <Select
                  value={interestAccountId}
                  onChange={(e) => setInterestAccountId(e.target.value)}
                  disabled={loadingSchedule}
                >
                  <option value="">Select expense nominal…</option>
                  {interestAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.accountNumber ? `${a.accountNumber} — ` : ''}
                      {a.accountName || a.account_name}
                    </option>
                  ))}
                </Select>
                {loanDetails?.interest_nominal_account_id && interestAccountId === loanDetails.interest_nominal_account_id && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    Defaulted from the loan&apos;s configured interest nominal.
                  </div>
                )}
              </div>

              {/* Reference + notes */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. DD-2026-04"
                  />
                </div>
                <div>
                  <FormLabel>Notes</FormLabel>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit}>
                  {busy ? 'Saving…' : 'Create loan match'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

export default LoanRepaymentModal;
